import { Dropdown, Button } from '@thegpvc/ui';
import { ChevronDown, Check } from 'lucide-react';

export interface FilterOption {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  /**
   * Text shown on the trigger button. Resolved by the caller so each filter
   * keeps its own value→label mapping (e.g. the source filter renders the
   * human `sourceLabel`, not the raw `codex` value).
   */
  triggerLabel: string;
  /**
   * Label of the leading reset item that clears this filter (e.g.
   * "All Projects"). Selecting it calls `onSelect('')`.
   */
  allLabel: string;
  /** Selectable options listed below the reset item. */
  options: FilterOption[];
  /**
   * The currently-applied filter value — `''` means no filter (the reset
   * item). The matching row in the open menu gets a check mark so the active
   * selection is visible, not just inferable from the trigger label. Defaults
   * to `''`.
   */
  value?: string;
  /** Called with `''` when the reset item is chosen, else the option's value. */
  onSelect: (value: string) => void;
}

/**
 * A menu row that shows its label and, when it is the active selection, a
 * trailing check mark plus an sr-only "(selected)" so screen readers announce
 * the current choice.
 */
function FilterItem({ label, selected }: { label: string; selected: boolean }) {
  return (
    <span className="flex flex-1 items-center justify-between gap-6">
      <span>{label}</span>
      {selected && (
        <>
          <Check className="h-4 w-4 shrink-0 text-orange-500" aria-hidden="true" />
          <span className="sr-only"> (selected)</span>
        </>
      )}
    </span>
  );
}

/**
 * A "select one of a bounded set, or all" filter control for the Sessions
 * header. Consolidates the identical Project/Device/Source dropdown scaffolding
 * (trigger button + reset item + separator + option list) so each filter is a
 * single declarative call rather than ~18 lines of repeated JSX.
 */
export function FilterDropdown({
  triggerLabel,
  allLabel,
  options,
  value = '',
  onSelect,
}: FilterDropdownProps) {
  return (
    <Dropdown>
      <Dropdown.Trigger asChild>
        <Button variant="secondary" size="sm">
          {triggerLabel}
          <ChevronDown className="w-4 h-4 ml-2" />
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Content align="end">
        <Dropdown.Item onClick={() => onSelect('')}>
          <FilterItem label={allLabel} selected={value === ''} />
        </Dropdown.Item>
        {options.length > 0 && <Dropdown.Separator />}
        {options.map((option) => (
          <Dropdown.Item key={option.value} onClick={() => onSelect(option.value)}>
            <FilterItem label={option.label} selected={option.value === value} />
          </Dropdown.Item>
        ))}
      </Dropdown.Content>
    </Dropdown>
  );
}
